/**
 * Post-submit reconciliation for optimistic CRM creates.
 *
 * After a create's event batch succeeds, the optimistic store entry holds only
 * the client-minted values — server-normalized/generated fields (display-name
 * formula, normalized email/phone, generated defaults) would stay stale because
 * the WS echo of our own submit carries the SAME seq as the optimistic write
 * and the store's seq guard skips it. These helpers hydrate the authoritative
 * entity from the server so the optimistic row reconciles with server state.
 *
 * Shared by both the service create paths (`services.ts`) and the generic
 * imperative funnel (`core/createObject.ts`) so every draft-aware create
 * reconciles identically. Lives in `core/` (not `services.ts`) to keep the
 * dependency direction one-way — `core` must never import from `services`.
 */

import type { DraftEntity } from '@/lib/crm/core/draftEntities';
import type { SubmitEventsResponse } from '@/types/crm';
import { isAsyncSingleObjectDeleteResponse } from '@/types/crm';

import {
  clearPendingCreations,
  getPendingCreationsSnapshot,
  getStoredObject,
  removeObject,
  upsertObject,
} from '../store/crmObjectStore';
import { createCRMObjectClient } from './CRMObjectClient';

/**
 * Reconcile the provisional optimistic entry after a create submit resolves.
 * Mirrors useCreateCrmObject. Two cases:
 *
 * - Backend applied the create onto a DIFFERENT (hidden) entity — dedup or
 *   overwrite_targets — so the client-generated `optimisticEntityId` never
 *   becomes real; its optimistic store entry + pendingCreations registration
 *   would otherwise linger as a phantom duplicate until pending cleanup. Remove
 *   it once the authoritative response proves it was rewritten, THEN hydrate the
 *   surviving entity via `client.get()` + `upsertObject()`. Without the hydrate,
 *   a create that dedups onto an entity the store never held would delete the
 *   provisional row and put nothing in its place — the row disappears entirely
 *   until a WS echo or list refetch happens to surface the survivor. Fetching it
 *   here makes the surviving record appear immediately. A hydrate failure is
 *   non-fatal (e.g. the survivor is permission-hidden): the create already
 *   succeeded, so we let the next WS event / refetch reconcile.
 * - Backend created the entity we asked for (no id rewrite): the optimistic
 *   entry holds only the client-minted values, and the WS echo of our own
 *   submit carries the SAME seq as the optimistic write, so the store's seq
 *   guard skips it — server-normalized / generated fields (e.g. the
 *   `ahoy_display_name` formula, normalized email/phone) would stay stale.
 *   Hydrate the authoritative entity via `client.get()` + `upsertObject()` —
 *   exactly as useCreateCrmObject does — so the optimistic row reconciles with
 *   server state. `upsertObject` lets authoritative fields win while still
 *   merging any concurrent WS edit that advanced the store past this fetch. A
 *   hydrate failure is non-fatal: the create already succeeded, so we keep the
 *   optimistic data and let the next WS event / refetch reconcile.
 */
export async function reconcileOptimisticCreate(
  orgId: number,
  entityTypeName: string,
  optimisticEntityId: string,
  response: SubmitEventsResponse
): Promise<void> {
  if (isAsyncSingleObjectDeleteResponse(response)) {
    // Object creation is never dispatched async; nothing to reconcile.
    return;
  }
  const resolvedEntityId = response.results?.[0]?.entity_id;
  const client = createCRMObjectClient(orgId, entityTypeName);
  if (resolvedEntityId && resolvedEntityId !== optimisticEntityId) {
    const phantomStillPresent =
      getStoredObject(optimisticEntityId) !== undefined ||
      getPendingCreationsSnapshot().has(optimisticEntityId);
    if (phantomStillPresent) {
      clearPendingCreations([optimisticEntityId]);
      removeObject(optimisticEntityId);
    }
    // Hydrate the surviving entity so removing the provisional row never leaves
    // a hole. If the store already holds the survivor (e.g. a visible existing
    // contact) this refreshes it; if it did not (a survivor the user never had
    // cached), this is what makes the record appear at all.
    try {
      upsertObject(await client.get(resolvedEntityId));
    } catch {
      // Non-fatal — the survivor may be permission-hidden or transiently
      // unavailable; the next WS event / list refetch reconciles it.
    }
    return;
  }

  try {
    const authoritative = await client.get(optimisticEntityId);
    upsertObject(authoritative);
  } catch {
    // Non-fatal — the create succeeded; leave the optimistic row for the next
    // WS event / refetch to reconcile.
  }
}

/**
 * Hydrate each folded draft after its atomic batch succeeds. A draft is
 * rendered from a seq-0 synthetic summary carrying only the client-minted
 * values, so without this its server-normalized fields (display-name formula,
 * normalized email/phone, generated defaults) stay stale until a WS echo /
 * list refresh. Unlike the parent create, a draft uses a client-minted
 * entity_id the backend preserves verbatim (no dedup/overwrite id-rewrite),
 * so each draft is fetched by its own id — the multi-entity batch response's
 * ordering is irrelevant and no `results[0]` rewrite handling is needed. Each
 * draft may be a different entity type, so a client is created per draft. A
 * hydrate failure is non-fatal: the create already committed, so the next WS
 * event / refetch reconciles it.
 */
export async function reconcileOptimisticDrafts(
  orgId: number,
  drafts: readonly DraftEntity[]
): Promise<void> {
  await Promise.all(
    drafts.map(async draft => {
      try {
        const client = createCRMObjectClient(orgId, draft.entity_type_name);
        upsertObject(await client.get(draft.entity_id));
      } catch {
        // Non-fatal — see the doc comment above.
      }
    })
  );
}
